import { BillingActualsOptions } from './BillingActualsOptions';
import {
  Dropdown,
  Stack,
  CommandBar,
  ICommandBarItemProps,
  TextField,
  Label,
  DetailsList,
  Text,
  IconButton,
  ITextField,
  IDropdown,
  FontIcon,
  Checkbox,
  ICheckbox,
  mergeStyles,
  getTheme,
  Pivot,
  PivotItem,
  Panel,
  PanelType,
  Separator,
  ChoiceGroup,
  IChoiceGroup,
  DefaultButton,
} from '@fluentui/react';
import {
  Dropdown as F9Dropdown,
  Option,
  OptionOnSelectData,
  SelectionEvents,
  Input,
  InputOnChangeData,
  Button,
  Tooltip,
  Spinner,
  MessageBar as FluentMessageBar,
  MessageBarBody as FluentMessageBarBody,
} from '@fluentui/react-components';
import { WizardSteps, useMutation, useWizard, type WizardStep } from '@moko/ui';
import { Controller, useForm } from 'react-hook-form';
import { useBoolean } from '@fluentui/react-hooks';
import EntityCell, { getEntityIcon } from './EntityCell';
import {
  IDragDropEvents,
  SelectionMode,
} from '@fluentui/react/lib/DetailsList';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import CodeMirror, { EditorView } from '@uiw/react-codemirror';
import { javascript } from '@codemirror/lang-javascript';
import { getReferences } from '../../services/referenceService';
import { generateEmptyReportTemplate } from '../../services/reportTemplateService';
import { localDownloadFile } from '@moko/utils';
import {
  getSpecificMetadata,
  revertMetadata,
  getMetadatasPaged,
  IField,
  IMetadata,
  IUpdateMetadataResponse,
  updateMetadataStatus,
  updateMetadata,
  exportMetadata,
  validateMetadata,
  IFieldsTab,
  IInteraction,
  getOrderComponentCatalog,
  getOrderWorkFormRelationships,
  getPublishedFormMetadatas,
  IOrderComponentDescriptor,
} from '../../services/metadataService';
import { buildEntityNameFilters, parseVersionParam } from './metadataHistory';
import MetadataHistoryPanel from './MetadataHistoryPanel';
import { getUserOrGroupsNames } from '../../services/userService';
import ConfirmDialog from '../../common/ConfirmDialog';
import { useSetAtom, useAtomValue } from 'jotai';
import {
  errorMessageAtom,
  isInProgressAtom,
  successMessageAtom,
} from '../../atoms/messageBarAtoms';
import AppContext from '../AppContext';
import UserOrGroupSelector from '../../common/selectors/UsersOrGroupSelectors';
import FieldsTabsEditor from './FieldsTabsEditor';
import InteractionsEditor from './InteractionsEditor';
import {
  labelColumnStyle,
  valueColumnStyle,
} from '../../common/styles/FormsStyles';
import FieldsEditorV2 from './FieldsEditorV2';
import FormPreviewPanel from './FormPreviewPanel';
import UpdateMetadataStatusPanel from './UpdateMetadataStatusPanel';
import CardDesignerPanel from './CardDesignerPanel';
import MapOptions from './MapOptions';
import OrderOptions from './OrderOptions';
import OrderWorkFormsPanel from './OrderWorkFormsPanel';
import WorkAssetFormsPanel from './WorkAssetFormsPanel';
import {
  METADATA_STATUS_TRANSITIONS,
  METADATA_STATUS_VISUALS,
  type MetadataStatus,
} from './metadataStatus';
import { profileDataAtom } from '../../atoms/authAtoms';

// Width of the create wizard, matched to the form builder so the top action
// bar aligns with the builder's right edge rather than the viewport edge.
const CREATE_WIZARD_MAX_WIDTH = 820;

const VALID_ENTITY_TYPES = [
  'Asset',
  'Contractor',
  'Group',
  'Issue',
  'Order',
  'Rent',
  'Renter',
  'User',
  'Work',
  'Zone',
];

type IInteractionSourceField = IField & {
  sourceEntityType?: string;
  sourceMetadataName?: string;
};

export const Metadata = () => {
  const [metadata, setMetadata] = useState<IMetadata>();
  const [orderMetadataFields, setOrderMetadataFields] = useState<
    IInteractionSourceField[]
  >([]);
  const [linkedCopiedFieldConflicts, setLinkedCopiedFieldConflicts] = useState<
    string[]
  >([]);
  const [linkedOrderFormsMessage, setLinkedOrderFormsMessage] =
    useState<string>();
  const [componentCatalog, setComponentCatalog] = useState<
    IOrderComponentDescriptor[]
  >([]);
  const [componentCatalogLoading, setComponentCatalogLoading] = useState(true);
  const [componentCatalogError, setComponentCatalogError] = useState<string>();
  const [metadataStr, setMetadataStr] = useState('');
  const [origMetadataStr, setOrigMetadataStr] = useState('');
  const [entityType, setEntityType] = useState('');
  const [name, setName] = useState('');
  const [statusSelection, setStatusSelection] = useState('Draft');
  const [statusComment, setStatusComment] = useState('');
  const [statusTarget, setStatusTarget] = useState<{
    metadataId: number;
    status: string;
    displayName?: string;
  }>();
  const [currFieldIndex, setCurrFieldIndex] = useState<number>();
  const [references, setReferences] = useState<string[]>([]);
  const [userOrGroupNames, setUserOrGroupNames] =
    useState<Map<string, string>>();
  const setSuccessMessage = useSetAtom(successMessageAtom);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const setIsInProgress = useSetAtom(isInProgressAtom);
  const isInProgress = useAtomValue(isInProgressAtom);
  const exportFile = useMutation(
    (
      request: { entityType: string; name: string; version: number },
      abortController: AbortController
    ) =>
      exportMetadata(
        abortController,
        request.entityType,
        request.name,
        request.version
      )
  );
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<
    string,
    string
  > | null>(null);

  const getAllowedStatusOptions = (status: string) => {
    const allowed = METADATA_STATUS_TRANSITIONS[status as MetadataStatus] ?? [
      'Draft',
    ];
    return allowed.map((value) => ({ key: value, text: value }));
  };

  const getAllowedStatusText = (status: string) => {
    const allowed = getAllowedStatusOptions(status).map(
      (option) => option.text
    );
    return `Allowed: ${status} -> ${allowed.join(' / ')}`;
  };

  const context = useContext(AppContext);
  const { currentTenant } = useAtomValue(profileDataAtom);
  const selectedTenantId =
    Number.isInteger(currentTenant) && currentTenant > 0
      ? currentTenant
      : undefined;
  const navigate = useNavigate();
  const location = useLocation();
  const routeParams = useParams<{ entity?: string; name?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const isCreateMode = location.pathname === '/metadata/new';
  const isEditMode = Boolean(routeParams.entity && routeParams.name);
  const [urlError, setUrlError] = useState<string>();
  const [latestVersion, setLatestVersion] = useState<number | null>(null);
  const [viewingVersion, setViewingVersion] = useState<number | null>(null);
  const [isHistoryOpen, setHistoryOpen] = useState(false);
  const [isRevertConfirmOpen, setRevertConfirmOpen] = useState(false);
  const [description, setDescription] = useState('');
  const [isNameEditable, setIsNameEditable] = useState(false);

  // React Hook Form drives the create-mode "Form details" step: required-field
  // validation, asterisks and the gate that blocks advancing to the builder.
  // Values are mirrored into the existing name/description/entityType state so
  // the rest of the component (save handler, designer) is untouched.
  const detailsForm = useForm<{
    name: string;
    description: string;
    entityType: string;
  }>({
    mode: 'onChange',
    defaultValues: { name: '', description: '', entityType: '' },
  });
  const detailsValid = detailsForm.formState.isValid;

  // Headless wizard logic for create mode. The builder step is disabled until
  // the details step is valid, so it renders greyed and unclickable.
  const wizardStepDescriptors: WizardStep[] = [
    {
      key: 'details',
      label: 'Form details',
      isCompleted: detailsValid,
      content: null,
    },
    {
      key: 'design',
      label: 'Form builder',
      // Not `disabled` (that would make it permanently unreachable and break
      // last-step / next-step math). Forward navigation to it is gated by
      // `canNavigateTo`, which requires the details step to be completed.
      lockedTooltip: 'Complete the form details first',
      isCompleted: ({ stepIndex, activeIndex }) => activeIndex >= stepIndex,
      content: null,
    },
  ];
  const wizard = useWizard({ steps: wizardStepDescriptors });
  const activeStep = wizard.activeKey as 'details' | 'design';

  const urlVersion = useMemo(
    () => parseVersionParam(searchParams.get('version')),
    [searchParams]
  );
  const isLatest = viewingVersion != null && viewingVersion === latestVersion;

  useEffect(() => {
    const abortController = new AbortController();
    getOrderComponentCatalog(abortController)
      .then(setComponentCatalog)
      .catch((error: unknown) => {
        if (abortController.signal.aborted) return;
        setComponentCatalogError(
          error instanceof Error
            ? error.message
            : 'Failed to load the Order Component catalogue.'
        );
      })
      .finally(() => {
        if (!abortController.signal.aborted) setComponentCatalogLoading(false);
      });
    return () => abortController.abort();
  }, []);

  const fetchData = async (abortController = new AbortController()) => {
    try {
      setIsInProgress(true);
      const references = await getReferences(abortController);
      setReferences(references);
    } finally {
      setIsInProgress(false);
    }
  };

  const currentStatus = metadata?.status ?? 'Draft';
  const statusVisual =
    METADATA_STATUS_VISUALS[currentStatus as MetadataStatus] ??
    METADATA_STATUS_VISUALS.Draft;
  const statusPanelStatus = statusTarget?.status ?? currentStatus;
  const interactionFieldData = useMemo(() => {
    const fields = new Map<string, IInteractionSourceField>(
      (metadata?.fields ?? []).map((field) => [field.name, { ...field }])
    );
    const conflicts = [...linkedCopiedFieldConflicts];

    for (const copiedField of orderMetadataFields) {
      const existing = fields.get(copiedField.name);
      if (!existing) {
        fields.set(copiedField.name, copiedField);
        continue;
      }
      if (existing.fieldType !== copiedField.fieldType) {
        conflicts.push(
          `${copiedField.name}: Work (${existing.fieldType}) conflicts with linked Order form field (${copiedField.fieldType}).`
        );
        continue;
      }
      fields.set(copiedField.name, {
        ...existing,
        sourceEntityType: 'Order',
        sourceMetadataName: copiedField.sourceMetadataName,
      });
    }

    return {
      fields: [...fields.values()],
      conflicts: [...new Set(conflicts)],
    };
  }, [linkedCopiedFieldConflicts, metadata?.fields, orderMetadataFields]);

  useEffect(() => {
    const abortController = new AbortController();
    fetchData(abortController);
    return () => abortController.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setUrlError(undefined);
    setIsNameEditable(false);
    if (isCreateMode) {
      setEntityType('');
      setName('');
      setDescription('');
      wizard.goTo('details');
      detailsForm.reset({ name: '', description: '', entityType: '' });
      setMetadata({
        lookups: [],
        fields: [],
        validations: [],
        interactions: [],
      } as IMetadata);
      setMetadataStr('');
      setOrigMetadataStr('');
      setLatestVersion(null);
      setViewingVersion(null);
      return;
    }
    if (isEditMode) {
      const decodedEntity = decodeURIComponent(routeParams.entity ?? '');
      const decodedName = decodeURIComponent(routeParams.name ?? '');
      if (!VALID_ENTITY_TYPES.includes(decodedEntity)) {
        setUrlError(`Unknown entity type "${decodedEntity}".`);
        return;
      }
      setEntityType(decodedEntity);
      setName(decodedName);
      const abortController = new AbortController();
      fetchEntityTypeData(
        decodedEntity,
        decodedName,
        urlVersion,
        abortController
      );
      return () => abortController.abort();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, urlVersion]);

  useEffect(() => {
    if (isCreateMode) {
      context.setSelectedTab('New Form');
      return;
    }
    if (isEditMode && entityType !== '') {
      context.setSelectedTab(name ? `${entityType}|${name}` : entityType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreateMode, isEditMode, entityType, name]);

  const fetchEntityTypeData = async (
    entityType: string,
    name: string,
    requestedVersion: number | null,
    abortController = new AbortController()
  ) => {
    try {
      setIsInProgress(true);
      // 1) Anchor on the listing: gives us the latest version for this
      // (entity, name) pair. Filters narrow to a single row; zero rows
      // means the metadata no longer exists -> redirect to the list.
      const listing = await getMetadatasPaged(
        abortController,
        10,
        1,
        undefined,
        undefined,
        buildEntityNameFilters(entityType, name)
      );
      if (abortController.signal.aborted) return;
      const row = listing?.items?.[0];
      if (!row) {
        navigate('/metadata', { replace: true });
        return;
      }
      const latest = row.version;
      setLatestVersion(latest);
      setDescription(row.comment ?? '');

      // 2) Decide which version to load. Invalid (>latest, <=0) -> latest.
      const target =
        requestedVersion != null && requestedVersion <= latest
          ? requestedVersion
          : latest;
      setViewingVersion(target);

      let data: IMetadata;
      try {
        data = await getSpecificMetadata(
          abortController,
          entityType,
          name,
          target,
          true,
          selectedTenantId
        );
      } catch {
        if (abortController.signal.aborted) return;
        // The specific version couldn't be loaded (e.g. removed/corrupt).
        // Fall back to latest so the user is never stuck on a broken URL.
        setErrorMessage(
          `Could not load version ${target}. Showing latest instead.`
        );
        if (target !== latest) {
          const next = new URLSearchParams(searchParams);
          next.delete('version');
          setSearchParams(next, { replace: true });
        }
        return;
      }
      if (abortController.signal.aborted) return;
      // Prefer the tenant-scoped response identity over the possibly broader
      // paged listing row.
      setMetadata({
        ...data,
        id: data.id ?? row.id,
        status: data.status ?? row.status,
      });
      if (entityType === 'Work') {
        setLinkedCopiedFieldConflicts([]);
        if (!selectedTenantId || !data.formDefinitionId) {
          setOrderMetadataFields([]);
          setLinkedOrderFormsMessage(
            'Select a tenant and ensure this Work form has a stable form identity to load linked Order fields.'
          );
        } else {
          setLinkedOrderFormsMessage(undefined);
          const publishedOrders = await getPublishedFormMetadatas(
            abortController,
            'Order',
            selectedTenantId
          );
          const orderLinks = await Promise.all(
            publishedOrders.map(async (form) => ({
              form,
              relationships:
                typeof form.metadata.formDefinitionId === 'number'
                  ? await getOrderWorkFormRelationships(
                      abortController,
                      form.metadata.formDefinitionId,
                      selectedTenantId
                    )
                  : [],
            }))
          );
          const linkedOrderForms = orderLinks.filter(({ relationships }) =>
            relationships.some(
              (relationship) =>
                relationship.workFormDefinitionId === data.formDefinitionId
            )
          );
          const copiedFields = linkedOrderForms.flatMap(({ form }) =>
            form.metadata.fields
              .filter((field) => field.copyToOrderedEntity)
              .map((field) => ({
                ...field,
                sourceEntityType: 'Order',
                sourceMetadataName: `${form.name} (#${form.metadata.formDefinitionId})`,
              }))
          );
          const fieldsByName = new Map<string, typeof copiedFields>();
          copiedFields.forEach((field) => {
            const existing = fieldsByName.get(field.name) ?? [];
            fieldsByName.set(field.name, [...existing, field]);
          });
          const incompatibleFields: string[] = [];
          const compatibleFields = [...fieldsByName.entries()].flatMap(
            ([fieldName, fields]) => {
              const fieldTypes = [
                ...new Set(fields.map((field) => field.fieldType)),
              ];
              if (fieldTypes.length > 1) {
                incompatibleFields.push(
                  `${fieldName}: ${fields
                    .map(
                      (field) =>
                        `${field.sourceMetadataName} (${field.fieldType})`
                    )
                    .join(', ')}`
                );
                return [];
              }
              const first = fields[0];
              return [
                {
                  ...first,
                  sourceMetadataName: fields
                    .map((field) => field.sourceMetadataName)
                    .join(', '),
                },
              ];
            }
          );
          setLinkedCopiedFieldConflicts(incompatibleFields);

          setOrderMetadataFields(compatibleFields);
        }
      } else {
        setOrderMetadataFields([]);
        setLinkedCopiedFieldConflicts([]);
        setLinkedOrderFormsMessage(undefined);
      }
      const permissions = data.fields
        .flatMap((f) => f.permissions)
        .filter((p): p is string => Boolean(p));
      if (permissions && permissions.length > 0) {
        const userOrGroupData = await getUserOrGroupsNames(
          abortController,
          permissions
        );
        if (userOrGroupData) {
          setUserOrGroupNames(new Map(Object.entries(userOrGroupData)));
        }
      }
      setMetadataStr(JSON.stringify(data, null, 2));
      setOrigMetadataStr(JSON.stringify(data, null, 2));
    } catch (error: any) {
      if (abortController.signal.aborted) return;
      console.error('Error:', error);
      setErrorMessage(error.message);
    } finally {
      if (!abortController.signal.aborted) setIsInProgress(false);
    }
  };
  const buildStatusTargetDisplayName = (
    targetEntityType: string,
    name?: string
  ) => {
    if (name && name.trim().length > 0) {
      return `${targetEntityType}|${name}`;
    }
    return targetEntityType;
  };

  const openStatusDialogForTarget = (target: {
    metadataId: number;
    status: string;
    displayName?: string;
  }) => {
    setStatusTarget(target);
    setStatusSelection(target.status);
    setStatusComment('');
    showStatusDialog();
  };

  const getStatusAction = (nextStatus: string) =>
    nextStatus === 'Draft' ? 'Unarchived' : nextStatus;

  const dismissStatusDialog = () => {
    setStatusTarget(undefined);
    setValidationErrors(null);
    hideStatusDialog();
  };

  const handleStatusUpdate = async () => {
    if (!statusTarget?.metadataId) {
      setErrorMessage('Metadata id is missing for status update.');
      return;
    }
    const abortController = new AbortController();
    setIsUpdatingStatus(true);
    setValidationErrors(null);
    try {
      if (
        statusSelection === 'Approved' &&
        metadata &&
        statusTarget.metadataId === metadata.id &&
        entityType
      ) {
        const validation = await validateMetadata(
          abortController,
          entityType,
          name,
          metadata
        );
        const fieldErrors = validation.errors ?? {};
        if (Object.keys(fieldErrors).length > 0) {
          setValidationErrors(fieldErrors);
          return;
        }
        if (validation.isSuccess === false) {
          setErrorMessage(validation.message ?? 'Validation failed.');
          return;
        }
      }

      const response = await updateMetadataStatus(
        abortController,
        statusTarget.metadataId,
        {
          status: statusSelection,
          action: getStatusAction(statusSelection),
          comment: statusComment.trim() || undefined,
        }
      );
      const nextStatus = response?.status ?? statusSelection;
      if (metadata?.id === statusTarget.metadataId) {
        setMetadata((prev) => (prev ? { ...prev, status: nextStatus } : prev));
      }
      setSuccessMessage(`Status updated to ${nextStatus}.`);
      await fetchData();
      dismissStatusDialog();
    } catch (error: any) {
      setErrorMessage(error.message);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const exportMetadataFile = async () => {
    const trimmedName = name.trim();
    if (!entityType || !trimmedName || viewingVersion == null) {
      setErrorMessage('Entity type, name, and version are required to export.');
      return;
    }
    const outcome = await exportFile.mutate({
      entityType,
      name: trimmedName,
      version: viewingVersion,
    });
    if (outcome.status === 'error') {
      setErrorMessage(outcome.error.message);
      return;
    }
    if (outcome.status !== 'success') return;
    const response = outcome.data;
    const header = response.headers.get('Content-Disposition');
    const named = header?.match(
      /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i
    );
    const fromHeader = named?.[1] ? decodeURIComponent(named[1]) : named?.[2];
    const fallback =
      `${entityType}-${trimmedName}-v${viewingVersion}.json`.replace(
        /[^\w.-]+/g,
        '_'
      );
    localDownloadFile(await response.blob(), fromHeader || fallback);
  };

  const downloadEmptyReportTemplate = async () => {
    const abortController = new AbortController();
    try {
      const response = await generateEmptyReportTemplate(abortController, name);
      const blob = await response.blob();
      localDownloadFile(blob, 'EmptyReportTemplate.docx');
    } catch (error: any) {
      setErrorMessage(error?.message || 'Failed to download report template.');
    }
  };

  /**
   * After Save / Revert: force a refetch of the latest version. Clearing
   * ?version alone is not enough on Save-from-latest (URL doesn't change,
   * effect doesn't re-run), so we also fetch directly. URL stays the
   * single source of truth — we just request a load of "latest" (null).
   */
  const navigateToLatest = () => {
    const next = new URLSearchParams(searchParams);
    const hadVersion = next.has('version');
    next.delete('version');
    if (hadVersion) {
      setSearchParams(next, { replace: true });
    } else if (entityType && name) {
      fetchEntityTypeData(entityType, name, null);
    }
  };

  const performRevert = () => {
    if (!metadata || viewingVersion == null) return;
    setIsInProgress(true);
    const abortController = new AbortController();
    revertMetadata(abortController, entityType, name, viewingVersion, metadata)
      .then((data: IUpdateMetadataResponse) => {
        setSuccessMessage(`Reverted. New version: ${data.version}`);
        navigateToLatest();
      })
      .catch((err) => setErrorMessage(err.message))
      .finally(() => setIsInProgress(false));
  };

  const _items: ICommandBarItemProps[] = [
    isCreateMode
      ? {
          key: 'create',
          name: 'Create',
          iconProps: { iconName: 'Add' },
          disabled: entityType === '' || name.trim() === '' || !metadata,
          onClick: () => {
            if (!metadata) return;
            const trimmedName = name.trim();
            if (entityType === '' || trimmedName === '') {
              setErrorMessage('Entity type and name are required.');
              return;
            }
            setIsInProgress(true);
            const abortController = new AbortController();
            updateMetadata(abortController, entityType, metadata, trimmedName)
              .then(async (data: IUpdateMetadataResponse) => {
                // The create response only carries the version, so we read the
                // freshly created metadata to obtain its id, then record the
                // provided Description as the Draft status comment (the list's
                // "Description" column reads from this status comment).
                const created = await getSpecificMetadata(
                  abortController,
                  entityType,
                  trimmedName,
                  data.version
                );
                if (created.id != null) {
                  await updateMetadataStatus(abortController, created.id, {
                    status: 'Draft',
                    action: getStatusAction('Draft'),
                    comment: description.trim() || undefined,
                  });
                }
                setSuccessMessage(`Version: ${data.version.toString()}`);
                navigate(
                  `/metadata/${encodeURIComponent(
                    entityType
                  )}/${encodeURIComponent(trimmedName)}`
                );
              })
              .catch((err) => setErrorMessage(err.message))
              .finally(() => setIsInProgress(false));
          },
        }
      : isLatest
      ? {
          key: 'save',
          name: 'Save',
          iconProps: { iconName: 'Save' },
          disabled: entityType === '' && metadataStr === origMetadataStr,
          onClick: () => {
            if (!metadata) return;
            const trimmedName = name.trim();
            if (trimmedName === '') {
              setErrorMessage('Name is required.');
              return;
            }
            const originalName = decodeURIComponent(routeParams.name ?? '');
            setIsInProgress(true);
            const abortController = new AbortController();
            updateMetadata(abortController, entityType, metadata, trimmedName)
              .then(async (data: IUpdateMetadataResponse) => {
                setSuccessMessage(`Version: ${data.version.toString()}`);
                setIsNameEditable(false);
                if (trimmedName !== originalName) {
                  navigate(
                    `/metadata/${encodeURIComponent(
                      entityType
                    )}/${encodeURIComponent(trimmedName)}`
                  );
                } else {
                  navigateToLatest();
                }
              })
              .catch((err) => setErrorMessage(err.message))
              .finally(() => setIsInProgress(false));
          },
        }
      : {
          key: 'revert',
          name: `Revert to version ${viewingVersion ?? ''}`,
          iconProps: { iconName: 'Undo' },
          disabled: entityType === '' || viewingVersion == null,
          onClick: () => setRevertConfirmOpen(true),
        },
    {
      key: 'updateStatus',
      name: 'Update Status',
      iconProps: { iconName: 'Sync' },
      disabled: entityType === '',
      onClick: () => {
        if (!metadata?.id) {
          setErrorMessage('Metadata id is missing for status update.');
          return;
        }
        openStatusDialogForTarget({
          metadataId: metadata.id,
          status: currentStatus,
          displayName: buildStatusTargetDisplayName(entityType, name),
        });
      },
    },
    ...(isLatest
      ? [
          {
            key: 'saveAs',
            name: 'Save As',
            iconProps: { iconName: 'SaveAs' },
            disabled: entityType === '',
            onClick: () => {
              setIsNameEditable(true);
            },
          },
        ]
      : []),
    {
      key: 'preview',
      name: 'Preview',
      disabled: entityType === '',
      iconProps: { iconName: 'PreviewLink' },
      onClick: () => {
        showPreview();
      },
    },
    {
      key: 'show',
      name: 'Show',
      disabled: entityType === '',
      iconProps: { iconName: 'Code' },
      onClick: () => {
        setMetadataStr(JSON.stringify(metadata, null, 2));
        showCodeDialog();
      },
    },
    {
      key: 'history',
      name: 'History',
      disabled:
        entityType === '' || latestVersion == null || latestVersion <= 1,
      iconProps: { iconName: 'History' },
      onClick: () => setHistoryOpen(true),
    },
    {
      key: 'refresh',
      name: 'Refresh',
      disabled: entityType === '',
      iconProps: { iconName: 'Refresh' },
      onClick: () => {
        if (metadataStr !== origMetadataStr) {
          showConfirmDialog();
        }
      },
    },
    {
      key: 'export',
      name: 'Export',
      disabled:
        entityType === '' ||
        name.trim() === '' ||
        viewingVersion == null ||
        exportFile.loading,
      iconProps: { iconName: 'CloudDownload' },
      onClick: () => {
        exportMetadataFile();
      },
    },
    {
      key: 'downloadReportTemplate',
      name: 'Download report template',
      iconProps: { iconName: 'WordDocument' },
      onClick: () => {
        downloadEmptyReportTemplate();
      },
    },
  ];

  const [
    isCodeDialogOpen,
    { setTrue: showCodeDialog, setFalse: hideCodeDialog },
  ] = useBoolean(false);
  const [isPreviewOpen, { setTrue: showPreview, setFalse: hidePreview }] =
    useBoolean(false);
  const [
    isStatusDialogOpen,
    { setTrue: showStatusDialog, setFalse: hideStatusDialog },
  ] = useBoolean(false);
  const [
    isConfirmDialogOpen,
    { setTrue: showConfirmDialog, setFalse: hideConfirmDialog },
  ] = useBoolean(false);
  const [sidePanel, setSidePanel] = useState<
    'LookupEditor' | 'FieldEditor' | 'PermissionEditor' | undefined
  >();

  const [
    isUsersSelectorOpen,
    { setTrue: showUsersSelector, setFalse: hideUsersSelector },
  ] = useBoolean(false);
  const [accesses, setAccesses] = useState<any[]>();
  const [tempField, setTempField] = useState<IField>();
  useEffect(() => {
    if (entityType === '' || !metadata) {
      if (!statusTarget) {
        setStatusSelection('Draft');
      }
      return;
    }
    if (!statusTarget) {
      setStatusSelection(currentStatus);
    }
  }, [entityType, currentStatus, metadata, statusTarget]);

  const FieldEditor = () => {
    interface IAccessWithIndex {
      index: number;
      id: number;
      name: string;
      isGroup: boolean;
    }

    const fieldNameRef = useRef<ITextField>(null);
    const fieldLabelRef = useRef<ITextField>(null);
    const fieldHintRef = useRef<ITextField>(null);
    const fieldTypeRef = useRef<IDropdown>(null);
    const fieldReferencesRef = useRef<IDropdown>(null);
    const fieldDependeesRef = useRef<IDropdown>(null);
    const fieldTabRef = useRef<IDropdown>(null);
    const fieldUserOrGroupOptionRef = useRef<IChoiceGroup>(null);

    const fieldMandatoryRef = useRef<ICheckbox>(null);
    const fieldShowInGridRef = useRef<ICheckbox>(null);
    const fieldShowInClientRef = useRef<ICheckbox>(null);
    const fieldShowInReportRef = useRef<ICheckbox>(null);
    const fieldLabelStackedRef = useRef<ICheckbox>(null);
    const fieldCopyToOrderedEntityRef = useRef<ICheckbox>(null);

    const [fieldError, setFieldError] = useState<string>();
    const [fieldType, setFieldType] = useState(tempField?.fieldType);

    return (
      <Stack style={{ width: '100%' }} tokens={{ childrenGap: 14 }}>
        <Text variant="large">Field properties</Text>

        <Stack horizontal tokens={{ childrenGap: 20 }} verticalAlign="baseline">
          <Label style={labelColumnStyle}>Name</Label>
          <TextField
            style={valueColumnStyle}
            defaultValue={tempField?.name}
            componentRef={fieldNameRef}
          />
        </Stack>
        <Stack horizontal tokens={{ childrenGap: 20 }} verticalAlign="baseline">
          <Label style={labelColumnStyle}>Field type</Label>
          <Dropdown
            style={valueColumnStyle}
            options={[
              { key: 'String', text: 'String' },
              { key: 'Number', text: 'Number' },
              { key: 'NumberWithUnit', text: 'NumberWithUnit' },
              { key: 'Lookup', text: 'Lookup' },
              { key: 'Image', text: 'Image' },
              { key: 'AutoComplete', text: 'AutoComplete Lookup' },
              { key: 'UserSelector', text: 'User Selector' },
              { key: 'Date', text: 'Date' },
              { key: 'Coordinate', text: 'Coordinate' },
              { key: 'Address', text: 'Address' },
              { key: 'UserGroupSelector', text: 'User/Group selector' },
            ]}
            // defaultSelectedKey={tempField?.fieldType}
            selectedKey={fieldType}
            componentRef={fieldTypeRef}
            onChange={(_ev, option) => {
              setFieldType(option?.key.toString() ?? '');
            }}
          />
        </Stack>
        <Stack horizontal tokens={{ childrenGap: 20 }} verticalAlign="baseline">
          <Label style={labelColumnStyle}>Label</Label>
          <TextField
            style={valueColumnStyle}
            defaultValue={tempField?.label}
            componentRef={fieldLabelRef}
          />
        </Stack>
        <Stack horizontal tokens={{ childrenGap: 20 }} verticalAlign="baseline">
          <Label style={labelColumnStyle}>Hint</Label>
          <TextField
            style={valueColumnStyle}
            defaultValue={tempField?.hint}
            componentRef={fieldHintRef}
          />
        </Stack>
        <Stack horizontal tokens={{ childrenGap: 20 }} verticalAlign="baseline">
          <Label style={labelColumnStyle}>Tab</Label>
          <Dropdown
            style={valueColumnStyle}
            defaultSelectedKey={tempField?.tabName}
            options={
              metadata?.tabs?.map((tab: IFieldsTab) => ({
                key: tab.tabName,
                text: tab.caption,
              })) ?? []
            }
            componentRef={fieldTabRef}
          />
        </Stack>
        <Stack
          horizontal
          tokens={{ childrenGap: 20 }}
          verticalAlign="baseline"
          style={{
            display:
              fieldType &&
              ['AutoComplete', 'Lookup', 'NumberWithUnit'].includes(fieldType)
                ? ''
                : 'none',
          }}
        >
          <Label style={labelColumnStyle}>Reference</Label>
          <Dropdown
            style={valueColumnStyle}
            options={references?.map((r) => ({ key: r, text: r })) ?? []}
            defaultSelectedKey={tempField?.reference}
            componentRef={fieldReferencesRef}
          />
        </Stack>
        <Stack
          horizontal
          tokens={{ childrenGap: 20 }}
          verticalAlign="baseline"
          style={{
            display: fieldType !== 'AutoComplete' ? 'none' : '',
          }}
        >
          <Label style={labelColumnStyle}>Dependees</Label>
          <Dropdown
            style={valueColumnStyle}
            options={
              metadata?.fields?.map((f) => ({ key: f.name, text: f.name })) ??
              []
            }
            defaultSelectedKeys={tempField?.dependees?.split('|')}
            multiSelect={true}
            componentRef={fieldDependeesRef}
          />
        </Stack>
        <Stack
          horizontal
          tokens={{ childrenGap: 20 }}
          verticalAlign="baseline"
          style={{
            display: fieldType !== 'UserGroupSelector' ? 'none' : '',
          }}
        >
          <Label style={labelColumnStyle}>
            Pick the field should be for User or Group selection
          </Label>
          <ChoiceGroup
            style={valueColumnStyle}
            defaultSelectedKey={tempField?.options}
            options={[
              { key: 'U', text: 'User' },
              { key: 'G', text: 'Group' },
            ]}
            componentRef={fieldUserOrGroupOptionRef}
          />
        </Stack>
        <br />
        <Checkbox
          label="Mandatory"
          defaultChecked={tempField?.mandatory}
          boxSide="end"
          componentRef={fieldMandatoryRef}
        />
        <br />
        <Stack horizontal tokens={{ childrenGap: 20 }}>
          <Checkbox
            label="Show in grid"
            defaultChecked={tempField?.showInGrid}
            boxSide="end"
            componentRef={fieldShowInGridRef}
          />
          <Checkbox
            label="Show in client"
            defaultChecked={tempField?.showInClient}
            boxSide="end"
            componentRef={fieldShowInClientRef}
          />
        </Stack>
        <Separator />
        <Text variant="large">Permissions</Text>
        <CommandBar
          items={[
            {
              key: 'addPermission',
              text: 'Add',
              iconProps: { iconName: 'Add' },
              onClick: () => {
                showUsersSelector();
              },
            },
          ]}
        />
        <DetailsList
          columns={[
            {
              key: 'name',
              fieldName: 'name',
              name: 'User|Group Name',
              minWidth: 120,
            },
            {
              key: 'id',
              fieldName: 'id',
              name: 'User|Group Id',
              minWidth: 110,
              onRender(item) {
                return (
                  <Stack horizontal grow={true} tokens={{ childrenGap: 5 }}>
                    <Text variant="large">{item.id}</Text>
                    <Text variant="small">
                      {item.isGroup ? `(Group)` : `(User)`}
                    </Text>
                  </Stack>
                );
              },
            },
            {
              key: 'action',
              name: 'Action',
              minWidth: 85,
              onRender: (item: IAccessWithIndex) => (
                <Stack horizontal>
                  <IconButton
                    iconProps={{ iconName: 'Delete' }}
                    onClick={() => {
                      const newAccesses = accesses?.filter(
                        (a) => a.id !== item.id
                      );
                      setAccesses(newAccesses);
                    }}
                  />
                </Stack>
              ),
            },
          ]}
          items={
            accesses?.map((a) => ({
              key: a.id,
              id: a.id,
              name: userOrGroupNames?.get(a.name),
              isGroup: a.isGroup,
            })) ?? []
          }
          selectionMode={SelectionMode.none}
        />
        <Stack horizontal horizontalAlign="end">
          <Text variant="medium" style={{ color: 'red' }}>
            {fieldError}
          </Text>
          <IconButton
            iconProps={{ iconName: 'CheckMark' }}
            onClick={() => {
              const selectedFieldType =
                fieldTypeRef.current?.selectedOptions[0].key.toString();
              if (!selectedFieldType) {
                setFieldError('Type should be selected');
                return;
              }
              const selectedFieldTab = fieldTabRef.current?.selectedOptions
                ?.length
                ? fieldTabRef.current?.selectedOptions[0].key.toString()
                : undefined;
              const selectedFieldReference =
                fieldReferencesRef.current?.selectedOptions[0]?.key.toString();
              setFieldError(undefined);
              if (
                ['Lookup', 'AutoComplete', 'NumberWithUnit'].includes(
                  selectedFieldType
                ) &&
                selectedFieldReference === undefined
              ) {
                setFieldError(
                  `Type selected as ${selectedFieldType}, but missing lookup`
                );
                return;
              }
              if (metadata && tempField) {
                const fields = metadata?.fields;
                const newOrEditedField: IField = {
                  name: fieldNameRef.current?.value ?? '',
                  label: fieldLabelRef.current?.value ?? '',
                  hint: fieldHintRef.current?.value ?? '',
                  tabName: selectedFieldTab ?? '',
                  fieldType: selectedFieldType ?? '',
                  lookupList: selectedFieldReference ?? '',
                  dependees: fieldDependeesRef.current?.selectedOptions
                    .map((o) => o.key)
                    .join('|'),
                  reference:
                    fieldReferencesRef.current?.selectedOptions[0]?.key?.toString() ??
                    '',
                  options:
                    selectedFieldType === 'UserGroupSelector'
                      ? fieldUserOrGroupOptionRef.current?.checkedOption?.key
                      : undefined,
                  mandatory: fieldMandatoryRef.current?.checked ?? false,
                  showInGrid: fieldShowInGridRef.current?.checked ?? false,
                  showInClient: fieldShowInClientRef.current?.checked ?? false,
                  showInReport: fieldShowInReportRef.current?.checked ?? false,
                  labelAbove: fieldLabelStackedRef.current?.checked ?? false,
                  copyToOrderedEntity:
                    fieldCopyToOrderedEntityRef.current?.checked ?? false,
                  permissions: accesses?.map(
                    (a) => `${a.isGroup ? 'G' : 'U'}${a.id}`
                  ),
                };
                if (currFieldIndex !== undefined) {
                  fields[currFieldIndex] = newOrEditedField;
                } else {
                  fields.push(newOrEditedField);
                }
                const newMetadata: IMetadata = {
                  ...metadata,
                  lookups: metadata.lookups ?? [],
                  fields: [...(metadata?.fields ?? [])],
                  validations: [...(metadata?.validations ?? [])],
                  tabs: [...(metadata.tabs ?? [])],
                  interactions: [...(metadata?.interactions ?? [])],
                  cardLayout: metadata?.cardLayout,
                  mapOptions: metadata?.mapOptions,
                  billingActuals: metadata?.billingActuals,
                };
                setMetadata(newMetadata);
                setMetadataStr(JSON.stringify(newMetadata, null, 2));
              }
              setSidePanel(undefined);
            }}
          />
          <IconButton
            iconProps={{ iconName: 'Cancel' }}
            onClick={() => {
              setCurrFieldIndex(undefined);
              setTempField(undefined);
              setSidePanel(undefined);
            }}
          />
        </Stack>
        <UserOrGroupSelector
          isOpen={isUsersSelectorOpen}
          hideUsersAndGroupsSelector={hideUsersSelector}
          onUserOrGroupSelected={(
            id: number,
            isGroup: boolean,
            username?: string
          ) => {
            const userOrGroupKey = `${isGroup ? 'G' : 'U'}${id}`;
            userOrGroupNames?.set(userOrGroupKey, username ?? '');
            if (accesses) {
              setAccesses([
                ...accesses,
                { id: id, name: userOrGroupKey, isGroup: isGroup, email: '' },
              ]);
            } else {
              setAccesses([
                { id: id, name: userOrGroupKey, isGroup: isGroup, email: '' },
              ]);
            }
          }}
        />
      </Stack>
    );
  };

  const FieldsEditor = () => {
    interface IFieldWithIndex extends IField {
      index: number;
    }

    let draggedItem: IField | undefined;
    const insertBeforeItem = (item: IField): void => {
      const insertIndex =
        metadata?.fields?.findIndex((f) => f.name === item.name) ?? -1;
      if (insertIndex === -1 || draggedItem === undefined) {
        return;
      }
      const newFields = metadata?.fields?.filter(
        (f) => f.name !== draggedItem?.name
      );
      newFields?.splice(insertIndex, 0, draggedItem);
      const newMetadata: IMetadata = {
        ...metadata,
        lookups: metadata?.lookups ?? [],
        fields: newFields ?? [],
        validations: metadata?.validations ?? [],
        tabs: metadata?.tabs ?? [],
        interactions: metadata?.interactions ?? [],
        cardLayout: metadata?.cardLayout,
        mapOptions: metadata?.mapOptions,
        billingActuals: metadata?.billingActuals,
      };
      setMetadata(newMetadata);
      setMetadataStr(JSON.stringify(newMetadata, null, 2));
    };
    const theme = getTheme();
    const dragEnterClass = mergeStyles({
      backgroundColor: theme.palette.neutralLight,
      borderTopColor: 'black',
      borderTopStyle: 'solid',
    });
    const getDragDropEvents = () => {
      return {
        canDrop: () => {
          return true;
        },
        canDrag: () => {
          return true;
        },
        onDrop: (item?: IField) => {
          if (draggedItem && item) {
            insertBeforeItem(item);
          }
        },
        onDragStart: (item?: any) => {
          if (typeof item.startIndex === 'undefined') {
            // skip group item
            draggedItem = item;
          }
        },
        onDragEnd: () => {
          draggedItem = undefined;
        },
        onDragEnter: () => {
          // return string is the css classes that will be added to the entering element.
          return dragEnterClass;
        },
      };
    };
    const dragDropEvents: IDragDropEvents = getDragDropEvents();

    return (
      <Stack>
        <CommandBar
          items={[
            {
              key: 'AddField',
              text: 'Add',
              disabled: entityType === '',
              iconProps: { iconName: 'Add' },
              onClick: () => {
                setTempField({
                  name: '',
                  label: '',
                  fieldType: '',
                  lookupList: '',
                  mandatory: false,
                  showInGrid: false,
                  showInClient: false,
                });
                setCurrFieldIndex(undefined);
                setAccesses([]);
                setSidePanel('FieldEditor');
              },
            },
          ]}
        />
        <DetailsList
          columns={[
            {
              key: 'name',
              name: 'Name',
              fieldName: 'name',
              minWidth: 100,
            },
            {
              key: 'fieldType',
              name: 'Type',
              fieldName: 'fieldType',
              minWidth: 80,
            },
            {
              key: 'label',
              name: 'Label',
              fieldName: 'label',
              minWidth: 100,
            },
            {
              key: 'source',
              name: 'Source',
              fieldName: 'lookupList',
              minWidth: 80,
              onRender: (item: IFieldWithIndex) => (
                <Stack>
                  <span>
                    {item.fieldType === 'Lookup'
                      ? item.lookupList
                      : item.fieldType === 'AutoComplete'
                      ? item.reference
                      : ''}
                  </span>
                  <Text variant="small">
                    {item.fieldType === 'Lookup'
                      ? '(lookup)'
                      : item.fieldType === 'AutoComplete'
                      ? '(source)'
                      : ''}
                  </Text>
                </Stack>
              ),
            },
            {
              key: 'mandatory',
              name: 'Mandatory',
              fieldName: 'mandatory',
              minWidth: 75,
              onRender: (item: IFieldWithIndex) =>
                item.mandatory ? (
                  <FontIcon
                    iconName="StatusCircleCheckmark"
                    style={{ width: 25, height: 25, fontSize: 25 }}
                  />
                ) : (
                  <></>
                ),
            },
            {
              key: 'showInGrid',
              name: 'Grid',
              fieldName: 'showInGrid',
              minWidth: 30,
              onRender: (item: IFieldWithIndex) =>
                item.showInGrid ? (
                  <FontIcon
                    iconName="StatusCircleCheckmark"
                    style={{ width: 25, height: 25, fontSize: 25 }}
                  />
                ) : (
                  <></>
                ),
            },
            {
              key: 'showInClient',
              name: 'Client',
              fieldName: 'showInClient',
              minWidth: 40,
              onRender: (item: IFieldWithIndex) =>
                item.showInClient ? (
                  <FontIcon
                    iconName="StatusCircleCheckmark"
                    style={{ width: 25, height: 25, fontSize: 25 }}
                  />
                ) : (
                  <></>
                ),
            },
            {
              key: 'action',
              name: 'Action',
              minWidth: 50,
              onRender: (item: IFieldWithIndex) => (
                <Stack horizontal>
                  <IconButton
                    iconProps={{ iconName: 'Edit' }}
                    onClick={() => {
                      const field = metadata?.fields?.find(
                        (p) => p.name === item.name
                      );
                      if (field?.permissions) {
                        setAccesses(
                          field.permissions.map((a) => ({
                            id: a.replace('U', '').replace('G', ''),
                            name: a,
                            isGroup: a.startsWith('G'),
                          }))
                        );
                      }
                      setCurrFieldIndex(item.index);
                      setTempField(item);
                      setSidePanel('FieldEditor');
                    }}
                  />
                  <IconButton
                    iconProps={{ iconName: 'Delete' }}
                    onClick={() => {
                      const newMetadata: IMetadata = {
                        ...metadata,
                        lookups: metadata?.lookups ?? [],
                        fields:
                          metadata?.fields.filter(
                            (f) => f.name !== item.name
                          ) ?? [],
                        validations: metadata?.validations ?? [],
                        tabs: metadata?.tabs ?? [],
                        interactions: metadata?.interactions ?? [],
                        cardLayout: metadata?.cardLayout,
                        mapOptions: metadata?.mapOptions,
                        billingActuals: metadata?.billingActuals,
                      };
                      setMetadata(newMetadata);
                      setMetadataStr(JSON.stringify(newMetadata, null, 2));
                    }}
                  />
                </Stack>
              ),
            },
          ]}
          items={metadata?.fields?.map((f, index) => ({ ...f, index })) ?? []}
          selectionMode={SelectionMode.none}
          dragDropEvents={dragDropEvents}
        />
      </Stack>
    );
  };

  void FieldsEditor;

  if (urlError) {
    return (
      <Stack tokens={{ childrenGap: 12 }} style={{ padding: 20 }}>
        <Text variant="large">Metadata not found</Text>
        <Text>{urlError}</Text>
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <DefaultButton
            text="Back to list"
            onClick={() => navigate('/metadata')}
          />
        </Stack>
      </Stack>
    );
  }

  const primaryAction = _items[0];

  const requiredLabel = (text: string) => <Label required>{text}</Label>;

  const detailsContent = (
    <Stack tokens={{ childrenGap: 16 }} style={{ maxWidth: 520 }}>
      <Controller
        control={detailsForm.control}
        name="name"
        rules={{
          required: 'Name is required',
          validate: (v) => v.trim() !== '',
        }}
        render={({ field, fieldState }) => (
          <Stack tokens={{ childrenGap: 4 }}>
            {requiredLabel('Name')}
            <Input
              value={field.value}
              onChange={(_ev: any, data: InputOnChangeData) => {
                field.onChange(data.value ?? '');
                setName(data.value ?? '');
              }}
              onBlur={field.onBlur}
              placeholder="New metadata name"
            />
            {fieldState.error && (
              <Text variant="small" style={{ color: 'red' }}>
                {fieldState.error.message}
              </Text>
            )}
          </Stack>
        )}
      />
      <Controller
        control={detailsForm.control}
        name="description"
        render={({ field }) => (
          <Stack tokens={{ childrenGap: 4 }}>
            <Label>Description</Label>
            <TextField
              multiline
              rows={3}
              value={field.value}
              onChange={(_ev, value) => {
                field.onChange(value ?? '');
                setDescription(value ?? '');
              }}
              placeholder="Enter your observations here..."
            />
          </Stack>
        )}
      />
      <Controller
        control={detailsForm.control}
        name="entityType"
        rules={{ required: 'Entity is required' }}
        render={({ field, fieldState }) => (
          <Stack tokens={{ childrenGap: 4 }}>
            {requiredLabel('Entity')}
            <F9Dropdown
              placeholder="Select entity"
              value={field.value}
              onOptionSelect={(
                _ev: SelectionEvents,
                data: OptionOnSelectData
              ) => {
                const next = (data?.optionValue ?? '').split('|')[0];
                field.onChange(next);
                setEntityType(next);
              }}
            >
              {VALID_ENTITY_TYPES.map((value) => (
                <Option key={value} value={value} text={value}>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    {getEntityIcon(value)}
                    {value}
                  </span>
                </Option>
              ))}
            </F9Dropdown>
            {fieldState.error && (
              <Text variant="small" style={{ color: 'red' }}>
                {fieldState.error.message}
              </Text>
            )}
          </Stack>
        )}
      />
    </Stack>
  );

  // Read-only summary of the "Form details" step, shown above the builder so
  // the user keeps context. Entity uses the shared EntityCell (same as the
  // list table and edit view).
  // Create-mode action bar. Owned here (not inside the Wizard lib) so the
  // consumer controls placement; on the builder step it sits in the preview
  // row right after the entity. Drives navigation via the headless wizard.
  const createActions = (
    <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
      <Button appearance="secondary" onClick={() => navigate('/metadata')}>
        Cancel
      </Button>
      {!wizard.isFirstStep && (
        <Button appearance="transparent" onClick={wizard.goBack}>
          Back
        </Button>
      )}
      {wizard.isLastStep ? (
        <Button
          appearance="primary"
          icon={
            isInProgress ? <Spinner size="tiny" /> : <FontIcon iconName="Add" />
          }
          disabled={primaryAction.disabled || isInProgress}
          onClick={() => primaryAction.onClick?.()}
        >
          Create
        </Button>
      ) : wizard.canGoNext ? (
        <Button appearance="primary" onClick={wizard.goNext}>
          Continue
        </Button>
      ) : (
        <Tooltip
          content="Complete the required form details first"
          relationship="description"
          withArrow
        >
          {/* Wrapper keeps the tooltip working while the button is disabled
              (disabled buttons don't emit pointer events). */}
          <span style={{ display: 'inline-flex' }}>
            <Button appearance="primary" disabled>
              Continue
            </Button>
          </span>
        </Tooltip>
      )}
    </Stack>
  );

  const createPreview = (
    <Stack
      horizontal
      verticalAlign="center"
      tokens={{ childrenGap: 24 }}
      style={{ marginBottom: 12 }}
    >
      <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
        <Label
          style={{
            color: getTheme().palette.neutralPrimary,
            fontWeight: 600,
          }}
        >
          Name
        </Label>
        <Text variant="medium">{name || '—'}</Text>
      </Stack>
      {entityType !== '' && (
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
          <Label
            style={{
              color: getTheme().palette.neutralPrimary,
              fontWeight: 600,
            }}
          >
            Entity
          </Label>
          <EntityCell entity={entityType} />
        </Stack>
      )}
    </Stack>
  );

  const designerBody = (
    <Stack horizontal verticalAlign="stretch">
      <Pivot defaultSelectedKey="fieldsV2">
        <PivotItem headerText="Fields" itemKey="fieldsV2">
          {['Order', 'Work'].includes(entityType) && !selectedTenantId && (
            <FluentMessageBar intent="warning">
              <FluentMessageBarBody>
                Select a tenant to manage Order Components and related Work
                forms.
              </FluentMessageBarBody>
            </FluentMessageBar>
          )}
          {entityType === 'Order' && componentCatalogError && (
            <FluentMessageBar intent="error">
              <FluentMessageBarBody>
                Could not load the Order Component catalogue:{' '}
                {componentCatalogError}
              </FluentMessageBarBody>
            </FluentMessageBar>
          )}
          <FieldsEditorV2
            metadata={metadata}
            setMetadata={setMetadata}
            setMetadataStr={setMetadataStr}
            references={references}
            entityType={selectedTenantId ? entityType : ''}
            componentCatalog={componentCatalog}
            componentCatalogLoading={componentCatalogLoading}
            componentCatalogError={componentCatalogError}
            currFieldIndex={currFieldIndex}
            setCurrFieldIndex={setCurrFieldIndex}
          />
        </PivotItem>
        {entityType === 'Order' && (
          <PivotItem headerText="Related Work Forms" itemKey="workForms">
            <OrderWorkFormsPanel
              orderFormDefinitionId={metadata?.formDefinitionId}
              tenantId={selectedTenantId}
            />
          </PivotItem>
        )}
        {entityType === 'Work' && (
          <PivotItem headerText="Related Asset Forms" itemKey="assetForms">
            <WorkAssetFormsPanel
              workFormDefinitionId={metadata?.formDefinitionId}
              tenantId={selectedTenantId}
            />
          </PivotItem>
        )}
        {/* <PivotItem headerText="Fields" itemKey="fields">
            <FieldsEditor />
          </PivotItem> */}
        <PivotItem headerText="Tabs" itemKey="tabs">
          <FieldsTabsEditor
            disabled={entityType === ''}
            fieldsTab={metadata?.tabs}
            onChange={(newValue: IFieldsTab[]) => {
              const newMetadata: IMetadata = {
                ...metadata,
                lookups: metadata?.lookups ?? [],
                fields: [...(metadata?.fields ?? [])],
                validations: [...(metadata?.validations ?? [])],
                tabs: newValue,
                interactions: metadata?.interactions ?? [],
                cardLayout: metadata?.cardLayout,
                mapOptions: metadata?.mapOptions,
                billingActuals: metadata?.billingActuals,
              };
              setMetadata(newMetadata);
              setMetadataStr(JSON.stringify(newMetadata, null, 2));
            }}
          />
        </PivotItem>
        <PivotItem headerText="Interactions" itemKey="interactions">
          {linkedOrderFormsMessage && (
            <FluentMessageBar intent="warning">
              <FluentMessageBarBody>
                {linkedOrderFormsMessage}
              </FluentMessageBarBody>
            </FluentMessageBar>
          )}
          {interactionFieldData.conflicts.length > 0 && (
            <FluentMessageBar intent="warning">
              <FluentMessageBarBody>
                Copied field keys with incompatible types are unavailable to new
                interactions. Existing interaction rules are preserved.{' '}
                {interactionFieldData.conflicts.join('; ')}
              </FluentMessageBarBody>
            </FluentMessageBar>
          )}
          <InteractionsEditor
            interactions={metadata?.interactions || []}
            fields={interactionFieldData.fields}
            tabs={metadata?.tabs || []}
            lookups={metadata?.lookups || []}
            disabled={entityType === ''}
            onChange={function (newValue: IInteraction[]): void {
              const newMetadata: IMetadata = {
                ...metadata,
                lookups: metadata?.lookups ?? [],
                fields: [...(metadata?.fields ?? [])],
                validations: [...(metadata?.validations ?? [])],
                tabs: [...(metadata?.tabs ?? [])],
                interactions: newValue,
                cardLayout: metadata?.cardLayout,
                mapOptions: metadata?.mapOptions,
                billingActuals: metadata?.billingActuals,
              };
              setMetadata(newMetadata);
              setMetadataStr(JSON.stringify(newMetadata, null, 2));
            }}
          />
        </PivotItem>
        {entityType === 'Work' && (
          <PivotItem headerText="Inspection Actuals" itemKey="billingActuals">
            <BillingActualsOptions
              metadata={metadata}
              onChange={(value) => {
                setMetadata(value);
                setMetadataStr(JSON.stringify(value, null, 2));
              }}
            />
          </PivotItem>
        )}
        <PivotItem headerText="Card Designer" itemKey="cardDesigner">
          <CardDesignerPanel
            metadata={metadata}
            entityType={entityType}
            setMetadata={(value) => setMetadata(value)}
            setMetadataStr={setMetadataStr}
            disabled={entityType === ''}
          />
        </PivotItem>
        {entityType === 'Order' && (
          <PivotItem headerText="Order Settings" itemKey="orderOptions">
            <OrderOptions
              metadata={metadata}
              setMetadata={setMetadata}
              setMetadataStr={setMetadataStr}
            />
          </PivotItem>
        )}
        <PivotItem headerText="Map Options" itemKey="mapOptions">
          <MapOptions
            metadata={metadata}
            setMetadata={(value) => setMetadata(value)}
            setMetadataStr={setMetadataStr}
            disabled={entityType === ''}
          />
        </PivotItem>
      </Pivot>
      <Panel
        isOpen={sidePanel !== undefined}
        onDismiss={() => setSidePanel(undefined)}
        closeButtonAriaLabel="Close"
        type={
          sidePanel === 'FieldEditor'
            ? PanelType.medium
            : PanelType.smallFixedFar
        }
        headerText={sidePanel}
      >
        {sidePanel === 'FieldEditor' ? <FieldEditor /> : <></>}
      </Panel>
    </Stack>
  );

  const designContent = (
    <Stack verticalAlign="stretch">
      {createPreview}
      {designerBody}
    </Stack>
  );

  const editHeader = (
    <Stack
      horizontal
      tokens={{ childrenGap: 10 }}
      verticalAlign="center"
      style={{ marginBottom: 12 }}
    >
      <Label
        style={{ paddingRight: 6 }}
        disabled={entityType === '' || !isLatest || !isNameEditable}
      >
        Name
      </Label>
      <Input
        style={{ width: 240 }}
        onChange={(_ev: any, data: InputOnChangeData) =>
          setName(data.value ?? '')
        }
        disabled={entityType === '' || !isLatest || !isNameEditable}
        value={name}
      />
      {entityType !== '' && <EntityCell entity={entityType} />}
      {entityType !== '' && (
        <Stack
          horizontal
          verticalAlign="center"
          tokens={{ childrenGap: 6 }}
          style={{ paddingLeft: 4 }}
        >
          <FontIcon
            iconName={statusVisual.iconName}
            style={{ color: statusVisual.color, fontSize: 16 }}
          />
          <Text variant="medium">{currentStatus}</Text>
        </Stack>
      )}
      {viewingVersion != null && (
        <Stack
          horizontal
          verticalAlign="center"
          tokens={{ childrenGap: 6 }}
          style={{ paddingLeft: 8 }}
        >
          <Text
            variant="medium"
            style={{
              color: getTheme().palette.themePrimary,
              fontWeight: 600,
            }}
          >
            v{viewingVersion}
          </Text>
          {isLatest ? (
            <Text
              variant="small"
              style={{
                background: getTheme().palette.themeLighter,
                color: getTheme().palette.themePrimary,
                padding: '2px 6px',
                borderRadius: 4,
              }}
            >
              Latest
            </Text>
          ) : (
            <Button
              size="small"
              appearance="outline"
              style={{ padding: '4px 10px' }}
              onClick={navigateToLatest}
            >
              Back to latest
            </Button>
          )}
        </Stack>
      )}
      <CommandBar
        items={[]}
        overflowItems={_items.slice(1)}
        overflowButtonProps={{
          ariaLabel: 'More actions',
          menuIconProps: { iconName: 'More' },
        }}
        styles={{ root: { padding: 0, background: 'transparent' } }}
      />
      <Button appearance="secondary" onClick={() => navigate('/metadata')}>
        Cancel
      </Button>
      {/* {isLatest && (
        <Button
          appearance="secondary"
          icon={<FontIcon iconName="SaveAs" />}
          disabled={isNameEditable}
          onClick={() => setIsNameEditable(true)}
        >
          Save As
        </Button>
      )} */}
      <Button
        appearance="primary"
        icon={
          <FontIcon iconName={primaryAction.iconProps?.iconName ?? 'Save'} />
        }
        disabled={primaryAction.disabled}
        onClick={() => primaryAction.onClick?.()}
      >
        {primaryAction.name}
      </Button>
    </Stack>
  );

  // Constrain the create wizard to the form builder's width so the top action
  // row aligns with the builder's right edge instead of sticking to the
  // viewport edge on wide screens.
  const createContent = (
    <Stack style={{ maxWidth: CREATE_WIZARD_MAX_WIDTH }}>
      <Stack
        horizontal
        horizontalAlign="space-between"
        verticalAlign="center"
        style={{ marginBottom: 8 }}
      >
        <WizardSteps
          steps={wizardStepDescriptors}
          activeKey={wizard.activeKey}
          completedByIndex={wizard.completedByIndex}
          canNavigateTo={wizard.canNavigateTo}
          onStepClick={wizard.goTo}
        />
        {/* Actions always live here next to the steps, so the button row is
            identical on both steps. */}
        {createActions}
      </Stack>
      {activeStep === 'details' ? detailsContent : designContent}
    </Stack>
  );

  return (
    <Stack>
      {isCreateMode ? (
        createContent
      ) : (
        <>
          {editHeader}
          {designerBody}
        </>
      )}
      <FormPreviewPanel
        isOpen={isPreviewOpen}
        onDismiss={hidePreview}
        metadata={metadata}
        componentCatalog={componentCatalog}
      />
      <Panel
        isOpen={isCodeDialogOpen}
        onDismiss={hideCodeDialog}
        type={PanelType.medium} // Or small/large as needed; medium fits code well
        closeButtonAriaLabel="Close"
        headerText="Code Preview" // Optional header
      >
        <div
          style={{
            width: '100%',
            maxWidth: '100%',
            overflowX: 'hidden',
            boxSizing: 'border-box',
          }}
        >
          <CodeMirror
            value={metadataStr}
            width="100%"
            height="100%" // Fill the panel body
            extensions={[
              javascript(),
              EditorView.lineWrapping, // Wrap long lines to prevent width expansion
            ]}
            onChange={(value) => setMetadataStr(value)}
            theme="light"
            basicSetup={{
              lineNumbers: true,
              foldGutter: true,
              highlightActiveLine: true,
            }}
            style={{
              fontFamily: '"Fira code", "Fira Mono", monospace',
              fontSize: 12,
              boxSizing: 'border-box',
              width: '100%',
            }}
          />
        </div>
      </Panel>
      <UpdateMetadataStatusPanel
        isOpen={isStatusDialogOpen}
        onDismiss={dismissStatusDialog}
        statusSelection={statusSelection}
        onStatusChange={(value) => {
          setStatusSelection(value);
          setValidationErrors(null);
        }}
        statusComment={statusComment}
        onCommentChange={(value) => setStatusComment(value)}
        allowedOptions={getAllowedStatusOptions(statusPanelStatus)}
        allowedText={getAllowedStatusText(statusPanelStatus)}
        targetDisplayName={statusTarget?.displayName}
        updateDisabled={!statusTarget?.metadataId}
        isUpdating={isUpdatingStatus}
        validationErrors={validationErrors}
        entityType={entityType}
        metadataName={name}
        onCancel={dismissStatusDialog}
        onUpdate={handleStatusUpdate}
      />
      <ConfirmDialog
        isModalOpen={isConfirmDialogOpen}
        hideModal={hideConfirmDialog}
        message="All your metadata changes would be lost. Are you sure."
        onYesClick={() => {
          fetchEntityTypeData(entityType, name, urlVersion);

          setSidePanel(undefined);
          hideConfirmDialog();
        }}
      />
      <ConfirmDialog
        isModalOpen={isRevertConfirmOpen}
        hideModal={() => setRevertConfirmOpen(false)}
        message={
          <Stack tokens={{ childrenGap: 8 }}>
            <Text>
              This will create a new version (including any edits you've made).
              Continue?
            </Text>
            {latestVersion != null && viewingVersion != null && (
              <Text
                style={{
                  color: getTheme().palette.themePrimary,
                  fontFamily:
                    '"Segoe UI", system-ui, -apple-system, sans-serif',
                }}
              >
                <strong>v{latestVersion}</strong>
                {' -> '}
                <strong>v{latestVersion + 1}</strong>
                {' (from '}
                <strong>v{viewingVersion}</strong>
                {')'}
              </Text>
            )}
          </Stack>
        }
        onYesClick={() => {
          setRevertConfirmOpen(false);
          performRevert();
        }}
      />
      {!isCreateMode && latestVersion != null && viewingVersion != null && (
        <MetadataHistoryPanel
          isOpen={isHistoryOpen}
          entity={entityType}
          name={name}
          latestVersion={latestVersion}
          currentVersion={viewingVersion}
          onSelect={(v) => {
            const next = new URLSearchParams(searchParams);
            if (v === latestVersion) next.delete('version');
            else next.set('version', String(v));
            setSearchParams(next);
            setHistoryOpen(false);
          }}
          onDismiss={() => setHistoryOpen(false)}
        />
      )}
    </Stack>
  );
};
